/**
 * ExpenseInvoiceFilters Component
 * Filter panel for expense invoices list
 */

import React from 'react';
import { Grid, TextField, MenuItem, Button, Box } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/pl';
import { FilterAltOff } from '@mui/icons-material';
import { ExpenseInvoiceStatus, ExpenseInvoiceQueryParams } from '../Types/expenseInvoice.types';

interface ExpenseInvoiceFiltersProps {
  filters: ExpenseInvoiceQueryParams;
  onFilterChange: (filters: ExpenseInvoiceQueryParams) => void;
  onClear: () => void;
}

const ExpenseInvoiceFilters: React.FC<ExpenseInvoiceFiltersProps> = ({
  filters,
  onFilterChange,
  onClear,
}) => {
  const handleStatusChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    onFilterChange({
      ...filters,
      status: value === '' ? undefined : (value as ExpenseInvoiceStatus),
    });
  };

  const handleSupplierNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onFilterChange({
      ...filters,
      supplierName: event.target.value || undefined,
    });
  };

  const handleSearchTermChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onFilterChange({
      ...filters,
      searchTerm: event.target.value || undefined,
    });
  };

  const handleInvoiceNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onFilterChange({
      ...filters,
      invoiceNumber: event.target.value || undefined,
    });
  };

  const handleSupplierCityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onFilterChange({
      ...filters,
      supplierCity: event.target.value || undefined,
    });
  };

  const handleDocumentKindChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    onFilterChange({
      ...filters,
      documentKind: value === '' ? undefined : (value as 'FullInvoice' | 'SimplifiedCost'),
    });
  };

  const handleIsAssignedToAnyBookingChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    onFilterChange({
      ...filters,
      isAssignedToAnyBooking: value === '' ? undefined : value === 'true',
    });
  };

  const handleIssueDateFromChange = (date: Dayjs | null) => {
    onFilterChange({
      ...filters,
      issueDateFrom: date ? date.format('YYYY-MM-DD') : undefined,
    });
  };

  const handleIssueDateToChange = (date: Dayjs | null) => {
    onFilterChange({
      ...filters,
      issueDateTo: date ? date.format('YYYY-MM-DD') : undefined,
    });
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pl">
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2}>
          {/* Status Filter */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }} >
            <TextField
              select
              fullWidth
              label="Status"
              value={filters.status || ''}
              onChange={handleStatusChange}
              size="small"
            >
              <MenuItem value="">Wszystkie</MenuItem>
              <MenuItem value={ExpenseInvoiceStatus.Draft}>Szkic</MenuItem>
              <MenuItem value={ExpenseInvoiceStatus.Received}>Otrzymana</MenuItem>
              <MenuItem value={ExpenseInvoiceStatus.Verified}>Zweryfikowana</MenuItem>
              <MenuItem value={ExpenseInvoiceStatus.Paid}>Opłacona</MenuItem>
              <MenuItem value={ExpenseInvoiceStatus.Cancelled}>Anulowana</MenuItem>
            </TextField>
          </Grid>

          {/* Supplier Name Filter */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }} >
            <TextField
              fullWidth
              label="Nazwa dostawcy"
              value={filters.supplierName || ''}
              onChange={handleSupplierNameChange}
              size="small"
              placeholder="Filtr dla pełnych faktur..."
            />
          </Grid>

          {/* Issue Date From */}
          <Grid size={{ xs: 12, sm: 6, md: 2 }} >
            <DatePicker
              label="Data wystawienia od"
              value={filters.issueDateFrom ? dayjs(filters.issueDateFrom) : null}
              onChange={handleIssueDateFromChange}
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                },
              }}
            />
          </Grid>

          {/* Issue Date To */}
          <Grid size={{ xs: 12, sm: 6, md: 2 }} >
            <DatePicker
              label="Data wystawienia do"
              value={filters.issueDateTo ? dayjs(filters.issueDateTo) : null}
              onChange={handleIssueDateToChange}
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                },
              }}
            />
          </Grid>

          {/* Search Term (general search) */}
          <Grid size={{ xs: 12, sm: 6, md: 2 }} >
            <TextField
              fullWidth
              label="Wyszukaj"
              value={filters.searchTerm || ''}
              onChange={handleSearchTermChange}
              size="small"
              placeholder="Referencja, opis..."
            />
          </Grid>

          {/* Invoice Number Filter */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }} >
            <TextField
              fullWidth
              label="Nr faktury dostawcy"
              value={filters.invoiceNumber || ''}
              onChange={handleInvoiceNumberChange}
              size="small"
            />
          </Grid>

          {/* Supplier City Filter */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }} >
            <TextField
              fullWidth
              label="Miasto dostawcy"
              value={filters.supplierCity || ''}
              onChange={handleSupplierCityChange}
              size="small"
            />
          </Grid>

          {/* Document Kind Filter */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }} >
            <TextField
              select
              fullWidth
              label="Rodzaj dokumentu"
              value={filters.documentKind || ''}
              onChange={handleDocumentKindChange}
              size="small"
            >
              <MenuItem value="">Wszystkie</MenuItem>
              <MenuItem value="FullInvoice">Faktura</MenuItem>
              <MenuItem value="SimplifiedCost">Koszt uproszczony</MenuItem>
            </TextField>
          </Grid>

          {/* Is Assigned To Any Booking Filter */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }} >
            <TextField
              select
              fullWidth
              label="Przypisanie do bookingu"
              value={filters.isAssignedToAnyBooking === undefined ? '' : String(filters.isAssignedToAnyBooking)}
              onChange={handleIsAssignedToAnyBookingChange}
              size="small"
            >
              <MenuItem value="">Wszystkie</MenuItem>
              <MenuItem value="true">Przypisana do bookingu</MenuItem>
              <MenuItem value="false">Nieprzypisana</MenuItem>
            </TextField>
          </Grid>

          {/* Clear Button */}
          <Grid size={{ xs: 12, md: 12 }} >
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<FilterAltOff />}
              onClick={onClear}
              size="small"
            >
              Wyczyść filtry
            </Button>
          </Grid>
        </Grid>
      </Box>
    </LocalizationProvider>
  );
};

export default ExpenseInvoiceFilters;
